/**
 * Admin → Personas list (§27.5).
 *
 * Lists all personas (including disabled). Each row links to the edit
 * form; the toolbar adds a "New persona" shortcut; per-row toggle flips
 * `enabled` via PATCH so admins can disable/re-enable without navigating
 * into the edit form.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  listAdminPersonas,
  setAdminPersonaEnabled,
} from "../services/admin.js";
import ErrorBubble from "../components/ErrorBubble.jsx";
import LoadingSkeleton from "../components/LoadingSkeleton.jsx";

export default function AdminPersonas() {
  const [personas, setPersonas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    return listAdminPersonas()
      .then((data) => {
        setPersonas(data.personas || []);
        setError(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function handleToggle(personaId, next) {
    const previous = personas;
    setTogglingId(personaId);
    setPersonas((list) =>
      list.map((p) => (p.id === personaId ? { ...p, enabled: next } : p)),
    );
    try {
      await setAdminPersonaEnabled(personaId, next);
      setError(null);
    } catch (err) {
      setError(err.message);
      setPersonas(previous);
    } finally {
      setTogglingId(null);
    }
  }

  return (
    <div className="admin-page">
      <div className="admin-page__header">
        <h1>Personas</h1>
        <Link to="/admin/personas/new" className="btn btn-primary">
          New persona
        </Link>
      </div>
      {error && <ErrorBubble message={error} />}
      {loading ? (
        <LoadingSkeleton variant="line" />
      ) : personas.length === 0 ? (
        <p className="admin-page__empty">No personas registered.</p>
      ) : (
        <table className="admin-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Category</th>
              <th>Enabled</th>
              <th>Version</th>
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {personas.map((p) => (
              <tr key={p.id || p.PK}>
                <td>
                  <Link to={`/admin/personas/${p.id}/edit`}>{p.name}</Link>
                </td>
                <td>{p.category}</td>
                <td>
                  <label className="admin-toggle">
                    <input
                      type="checkbox"
                      checked={Boolean(p.enabled)}
                      disabled={togglingId === p.id}
                      onChange={(e) => handleToggle(p.id, e.target.checked)}
                      aria-label={`${p.enabled ? "Disable" : "Enable"} ${p.name}`}
                    />
                    <span>{p.enabled ? "Enabled" : "Disabled"}</span>
                  </label>
                </td>
                <td>{p.version ?? "—"}</td>
                <td />
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
